/**
 * Push, decided once for both ends.
 *
 * Two separate things live here because they are the two halves of one
 * feature and both of them are rules rather than plumbing:
 *
 *   1. **What a notification says.** `server/push.ts` builds the payload and
 *      `src/sw.ts` renders it, and if those two ever held their own copy of
 *      the sentence they would drift — the worker is cached from an older
 *      build than the server it is talking to, permanently and by design. So
 *      the server sends the *event*, the worker turns it into words, and the
 *      words are `notificationFor`, which is imported by both. It is the same
 *      arrangement `src/lib/theme.ts` and `src/lib/motion.ts` are in.
 *
 *   2. **Whether to offer it at all**, which on iOS is the whole problem.
 *
 * ## The iOS sentence, and why it is not optional
 *
 * Web Push is free and immediate on Android and on the desktop. On iOS it
 * exists only for a site that has been **added to the Home Screen** — Safari
 * in a tab has no `PushManager` at all, and calling `Notification.requestPermission`
 * there either throws or resolves to something that never delivers anything.
 *
 * A prompt that says "turn on notifications" to somebody in Safari on an
 * iPhone is therefore a button that cannot work, and the version of this that
 * ships everywhere — ask anyway, fail quietly — is the one that teaches people
 * the feature is broken. So `availability` names `needs-install` as a state of
 * its own, before permission is ever asked for, and `describePush` gives it
 * the two sentences that are actually true: what to do, and why it is
 * necessary. The button is not offered until it would do something.
 */

import { formatMoney } from './format'

// ---------------------------------------------------------------------------
// What a notification says
// ---------------------------------------------------------------------------

/**
 * The four things this app will interrupt somebody for.
 *
 * There is no fifth, and adding one should be an argument. A notification is
 * a tap on the shoulder about other people's money; the bar is that the person
 * would want to know now rather than next time they open the app.
 *
 * `settlement.rejected` is the fourth, and the argument for it is that it is
 * the only event here that moves somebody's balance *against* them without
 * their doing anything: they recorded a payment, the person they paid said it
 * never arrived, and what they owe has gone back up. Finding that out by
 * chance, days later, is exactly the surprise this app exists to prevent.
 */
export type PushEvent =
  /** Somebody in a group you are in filed something you are part of. */
  | {
      kind: 'expense.added'
      actor: string
      groupId: string
      groupName: string
      transactionId: string
      description: string
      amountCents: number
      /** Your share of it, or null when you are not on the owing side. */
      shareCents: number | null
      currency: string
    }
  /** Somebody recorded paying *you*. The balance has already moved. */
  | {
      kind: 'settlement.recorded'
      actor: string
      groupId: string
      groupName: string
      transactionId: string
      amountCents: number
      currency: string
      /** The project the payment was tagged to, if any — what it was for. */
      projectName?: string | null
    }
  /** They are still waiting for you to say it turned up. */
  | {
      kind: 'settlement.nudge'
      actor: string
      groupId: string
      groupName: string
      transactionId: string
      amountCents: number
      currency: string
      /** The project the payment was tagged to, if any — what it was for. */
      projectName?: string | null
    }
  /**
   * The person you recorded paying says it never arrived, so the payment has
   * been removed and you owe it again. `actor` is them, not you.
   */
  | {
      kind: 'settlement.rejected'
      actor: string
      groupId: string
      groupName: string
      transactionId: string
      amountCents: number
      currency: string
      /** The project the payment was tagged to, if any — what it was for. */
      projectName?: string | null
    }

/** A notification, in the four fields `showNotification` actually wants. */
export type PushNotice = {
  title: string
  body: string
  /**
   * Two notifications with the same tag replace one another rather than
   * stacking. It is the transaction's id, so a nudge about a payment lands on
   * top of the "Marcus paid you" it is chasing instead of beside it.
   */
  tag: string
  /** Where tapping it goes — a path in this app, never an origin. */
  url: string
}

/**
 * The words, from the event. Every string this app is ever allowed to put on
 * somebody's lock screen is in this function.
 */
export function notificationFor(event: PushEvent): PushNotice {
  const money = formatMoney(event.amountCents, event.currency)
  const url = `/groups/${encodeURIComponent(event.groupId)}`
  // A settlement made from a project says which one, beside the group: "for
  // the food" is what tells somebody owed by the same person twice which
  // payment this is.
  const where =
    event.kind !== 'expense.added' && event.projectName
      ? `${event.groupName} · for ${event.projectName}`
      : event.groupName

  switch (event.kind) {
    case 'expense.added':
      return {
        title: `${event.actor} added an expense`,
        body:
          event.shareCents === null || event.shareCents === 0
            ? `${event.description} · ${money} · ${event.groupName}`
            : `${event.description} · ${money} · your share ${formatMoney(
                event.shareCents,
                event.currency,
              )}`,
        tag: `expense:${event.transactionId}`,
        url,
      }
    case 'settlement.recorded':
      return {
        title: `${event.actor} paid you`,
        body: `${money} · ${where} · confirm it when it lands`,
        tag: `settlement:${event.transactionId}`,
        url,
      }
    case 'settlement.nudge':
      return {
        title: 'Confirm this payment landed',
        body: `${event.actor} says they paid you ${money} · ${where}`,
        tag: `settlement:${event.transactionId}`,
        url,
      }
    // The same tag as the two above, deliberately: this is the last word on
    // that one payment, and it should land on top of "confirm it when it
    // lands" rather than beside a sentence it has just made untrue.
    case 'settlement.rejected':
      return {
        title: `${event.actor} did not get your payment`,
        body: `${money} · ${where} · it has been removed, so you owe it again`,
        tag: `settlement:${event.transactionId}`,
        url,
      }
  }
}

// ---------------------------------------------------------------------------
// Whether to offer it
// ---------------------------------------------------------------------------

/**
 * What the browser can do, as facts rather than as user-agent strings. The
 * caller — `src/push-document.ts` — reads all of these off the real browser;
 * a test hands them in.
 */
export type PushEnvironment = {
  /** `navigator.serviceWorker` exists. Nothing works without it. */
  serviceWorker: boolean
  /** `PushManager` in `window`. Absent in Safari on iOS *in a tab*. */
  pushManager: boolean
  /** `Notification` in `window`. */
  notification: boolean
  /** `Notification.permission`, or null when there is no `Notification`. */
  permission: 'default' | 'granted' | 'denied' | null
  /**
   * Running as an installed app — `display-mode: standalone` matched, or
   * `navigator.standalone`, which is iOS's own older answer to the same
   * question and the one that is reliable there.
   */
  installed: boolean
  /** iOS or iPadOS. The one platform where install comes first. */
  ios: boolean
  /** A live subscription is already on record for this browser. */
  subscribed: boolean
  /** The server has VAPID keys configured. Without them there is no push. */
  configured: boolean
}

export type PushState =
  /** Already on. Nothing to ask for; offer to turn it off. */
  | 'subscribed'
  /** Everything is in place. Ask. */
  | 'ready'
  /** iOS, in a tab. Install first — and say why, because it is not obvious. */
  | 'needs-install'
  /** They said no. The browser will not ask again; only settings can undo it. */
  | 'denied'
  /** This browser cannot, or this deployment has no keys. Say nothing at all. */
  | 'unsupported'

/**
 * Which of the five situations this browser is in.
 *
 * The order of the checks is the argument. `needs-install` is decided
 * **before** permission, because on iOS in a tab there is no permission to
 * have: asking would be a dialogue that cannot be answered usefully, and
 * reporting `denied` for it would be blaming somebody for a choice they were
 * never offered.
 */
export function availability(env: PushEnvironment): PushState {
  if (!env.configured) return 'unsupported'
  if (!env.serviceWorker || !env.notification) return 'unsupported'
  if (env.subscribed) return 'subscribed'
  if (!env.pushManager) return env.ios && !env.installed ? 'needs-install' : 'unsupported'
  if (env.ios && !env.installed) return 'needs-install'
  if (env.permission === 'denied') return 'denied'
  return 'ready'
}

/** The card: what it says, and what the button on it does. */
export type PushCopy = {
  headline: string
  detail: string
  /** The button's words, or null when there is nothing to press. */
  action: string | null
}

export function describePush(state: PushState): PushCopy | null {
  switch (state) {
    case 'unsupported':
      // Nothing is drawn at all. A card explaining that a browser cannot do
      // something is a card about us, not about them.
      return null
    case 'subscribed':
      return {
        headline: 'Notifications are on',
        detail:
          'You’ll hear when somebody adds an expense, pays you, or is waiting for you to confirm a payment landed.',
        action: 'Turn them off',
      }
    case 'ready':
      return {
        headline: 'Get told when something happens',
        detail:
          'An expense added, a payment made to you, a payment waiting on your word. Nothing else.',
        action: 'Turn on notifications',
      }
    case 'needs-install':
      return {
        headline: 'Add FriendLoan to your Home Screen first',
        detail:
          'iPhones and iPads only deliver notifications to apps that have been added to the Home Screen. Tap Share, then “Add to Home Screen”, and open FriendLoan from there — the button to turn notifications on is waiting inside.',
        action: null,
      }
    case 'denied':
      return {
        headline: 'Notifications are blocked',
        detail:
          'This browser was told not to allow them, and only it can change its mind — look for FriendLoan in its site settings.',
        action: null,
      }
  }
}

// ---------------------------------------------------------------------------
// The application server key
// ---------------------------------------------------------------------------

/**
 * The VAPID public key as `subscribe()` wants it: the same 65 bytes the server
 * publishes as base64url, as a `Uint8Array`.
 *
 * `PushManager.subscribe` will take a string in some browsers and not in
 * others, which is exactly the kind of difference that turns into "push works
 * on my phone". Converting here means one shape goes in everywhere, and it
 * means a malformed key is a named error at the call site rather than a
 * `DOMException` from inside the browser.
 */
export function applicationServerKey(base64url: string): Uint8Array {
  const normalised = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalised + '='.repeat((4 - (normalised.length % 4)) % 4)
  let binary: string
  try {
    binary = atob(padded)
  } catch {
    throw new Error('the push key the server sent is not base64url')
  }
  if (binary.length !== 65 || binary.charCodeAt(0) !== 0x04) {
    throw new Error('the push key the server sent is not an uncompressed P-256 point')
  }
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}
